/**
 * La reprise d'ouverture 2026, telle que l'écran la lit — et ce qu'il en
 * conclut sans aller-retour.
 *
 * ## La base est seule juge
 *
 * Tout ce que ce module calcule, la base le refuse d'elle-même :
 * `reprises_controle` et `reprises_controle_clients` à la validation,
 * `reprise_lignes_saisie` et `reprise_nc_controle` à chaque saisie. Ce module
 * rend le même verdict **avant** l'envoi, pour que l'écran dise ce qui
 * manque pendant qu'on travaille, avec les mêmes étiquettes (`[reprise-...]`)
 * que les messages de la base.
 *
 * ## Deux totaux, qui ne disent pas la même chose
 *
 * - **retenu** : le montant validé, à défaut le calculé — ce que l'ouverture
 *   porterait si tout était validé tel quel ;
 * - **validé** : ce qui est arrêté. C'est lui que la base met en balance.
 *
 * Le bilan d'ouverture ne s'invente pas : l'écart entre actif et passif n'est
 * jamais comblé par l'écran. Il se lit, et il se résout par les chiffres de
 * la comptable (état du patrimoine, extraits, procès-verbal de caisse).
 */

export type StatutReprise = 'brouillon' | 'validee' | 'comptabilisee';
export type Sens = 'debit' | 'credit';
export type Origine = 'calcul' | 'manuel';
/** `libre` : toute saisie justifiée ; `hors_cdv` : seulement pour un tiers sans compte CDV. */
export type ModeDeSaisie = 'libre' | 'hors_cdv';
export type EtatLigne = 'validee' | 'a_revoir' | 'a_valider' | 'en_attente';
export type CategorieNc =
  | 'opening_receivable_adjustment'
  | 'opening_family_liability'
  | 'current_year_prior_period_correction'
  | 'needs_review';

export interface Exercice {
  id: string;
  annee: number;
  dateDebut: string;
}

export interface Reprise {
  id: string;
  exerciceId: string;
  dateArrete: string;
  statut: StatutReprise;
  valideeLe: string | null;
  comptabiliseeLe: string | null;
  ecritureId: string | null;
}

export interface Rubrique {
  code: string;
  libelle: string;
  ordre: number;
  /** Proposée par le calcul depuis les faits de CDV (familles). */
  calculee: boolean;
  saisie: ModeDeSaisie;
  /** Débuts de code admis ; `null` : tout compte de bilan hors rubriques calculées. */
  prefixes: string[] | null;
  sensHabituel: Sens;
  aide: string | null;
}

export interface CompteDeBilan {
  code: string;
  libelle: string;
  classe: number;
  tiersObligatoire: boolean;
}

export interface LigneDeReprise {
  id: string;
  rubrique: string;
  rubriqueLibelle: string | null;
  compteCode: string;
  compteLibelle: string;
  tiersId: string | null;
  tiersNom: string | null;
  tiersType: string | null;
  userId: string | null;
  sens: Sens;
  origine: Origine;
  libelle: string | null;
  montantCalculeCents: number | null;
  montantValideCents: number | null;
  montantRetenuCents: number | null;
  /** Part reprise en créance douteuse (407000). */
  douteuxCents: number;
  /** Réduction de valeur actée au 31/12/2025 (409000), au plus le douteux. */
  reductionValeurCents: number;
  /** Part éteinte avant l'ouverture : hors ouverture. */
  eteintCents: number;
  aRevoir: string | null;
  justification: string | null;
  etat: EtatLigne;
  valideLe: string | null;
}

export interface LigneDeSynthese {
  rubrique: string;
  rubriqueLibelle: string | null;
  rubriqueOrdre: number | null;
  calculee: boolean | null;
  /** Le compte EFFECTIF : 407000 et 409000 pour une créance douteuse. */
  compteCode: string;
  compteLibelle: string;
  sens: Sens;
  nbLignes: number;
  nbCalculees: number;
  nbSaisies: number;
  nbValidees: number;
  nbAValider: number;
  nbARevoir: number;
  nbEnAttente: number;
  totalCalculeCents: number;
  totalValideCents: number;
  totalRetenuCents: number;
  totalEteintCents: number;
}

export interface NoteDeTransition {
  /** L'identifiant de la catégorisation ; `null` tant que la proposition n'a pas été calculée. */
  repriseNcId: string | null;
  creditNoteId: string;
  numero: string;
  date: string;
  montantCents: number;
  factureNumero: string | null;
  factureDate: string | null;
  factureMontantCents: number | null;
  payeAvantCents: number;
  rembourseAvantCents: number;
  creanceAvantCents: number;
  categorie: CategorieNc;
  categorieProposee: CategorieNc;
  validee: boolean;
  ajustementCents: number;
  detteCents: number;
  correctionCents: number;
  raison: string | null;
  blocage: string | null;
  justification: string | null;
  valideLe: string | null;
}

/** Un tiers client dont la reprise diffère de l'auxiliaire. */
export interface EcartClient {
  userId: string | null;
  tiersId: string | null;
  nom: string | null;
  segment: 'famille' | 'hors_cdv';
  grandLivreCents: number;
  notesCents: number;
  eteintesCents: number;
  attenduCents: number;
  repriseCents: number;
  ecartCents: number;
}

// ---------------------------------------------------------------------------
//  Libellés
// ---------------------------------------------------------------------------

export const STATUTS: Record<StatutReprise, { label: string; classe: string }> = {
  brouillon: { label: 'En préparation', classe: 'bg-amber-100 text-amber-800' },
  validee: { label: 'Validée', classe: 'bg-emerald-100 text-emerald-800' },
  comptabilisee: { label: 'Comptabilisée', classe: 'bg-indigo-100 text-indigo-800' },
};

export const ETATS_LIGNE: Record<EtatLigne, { label: string; classe: string }> = {
  validee: { label: 'Validée', classe: 'bg-emerald-100 text-emerald-800' },
  a_revoir: { label: 'À revoir', classe: 'bg-red-100 text-red-800' },
  a_valider: { label: 'À valider', classe: 'bg-amber-100 text-amber-800' },
  en_attente: { label: 'En attente du chiffre', classe: 'bg-gray-100 text-gray-700' },
};

export const CATEGORIES_NC: Record<CategorieNc, { label: string; aide: string }> = {
  opening_receivable_adjustment: {
    label: 'Ajustement de la créance d’ouverture',
    aide: 'La note réduit une créance encore due au 31/12/2025 : le 400000 d’ouverture est diminué d’autant.',
  },
  opening_family_liability: {
    label: 'Dette famille d’ouverture',
    aide: 'La note rend de l’argent déjà encaissé en 2025 : il est dû à la famille dès l’ouverture (460000).',
  },
  current_year_prior_period_correction: {
    label: 'Correction d’exercice antérieur',
    aide: 'Une opération 2026 marquée « exercice 2025 » : l’ouverture ne bouge pas.',
  },
  needs_review: {
    label: 'À revoir',
    aide: 'Le calcul ne tranche pas : une décision humaine, justifiée, est nécessaire.',
  },
};

/** Les catégories qu'un humain peut retenir : trancher, c'est sortir de `needs_review`. */
export const CATEGORIES_VALIDABLES: readonly CategorieNc[] = [
  'opening_receivable_adjustment',
  'opening_family_liability',
  'current_year_prior_period_correction',
] as const;

export const SENS: Record<Sens, string> = { debit: 'Débit', credit: 'Crédit' };

// ---------------------------------------------------------------------------
//  L'équilibre et ce qui empêche de valider
// ---------------------------------------------------------------------------

export interface Equilibre {
  debitRetenuCents: number;
  creditRetenuCents: number;
  /** Débit moins crédit, sur le retenu. Positif : il manque du passif ou des fonds propres. */
  ecartRetenuCents: number;
  debitValideCents: number;
  creditValideCents: number;
  ecartValideCents: number;
}

export function equilibre(synthese: readonly LigneDeSynthese[]): Equilibre {
  let dr = 0;
  let cr = 0;
  let dv = 0;
  let cv = 0;
  for (const s of synthese) {
    if (s.sens === 'debit') {
      dr += s.totalRetenuCents;
      dv += s.totalValideCents;
    } else {
      cr += s.totalRetenuCents;
      cv += s.totalValideCents;
    }
  }
  return {
    debitRetenuCents: dr,
    creditRetenuCents: cr,
    ecartRetenuCents: dr - cr,
    debitValideCents: dv,
    creditValideCents: cv,
    ecartValideCents: dv - cv,
  };
}

export interface Bloquant {
  /** L'étiquette que la base donnerait au même refus. */
  etiquette:
    | '[reprise-nc-a-trancher]'
    | '[reprise-lignes-a-valider]'
    | '[reprise-desequilibre]'
    | '[reprise-controle-clients]';
  message: string;
}

function pluriel(n: number, un: string, plusieurs: string): string {
  return `${n} ${n > 1 ? plusieurs : un}`;
}

function eur(cents: number): string {
  const signe = cents < 0 ? '-' : '';
  const a = Math.abs(cents);
  const entier = Math.floor(a / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${signe}${entier},${(a % 100).toString().padStart(2, '0')} €`;
}

/**
 * Ce qui empêche la validation, dans l'ordre où la base le dirait : notes à
 * trancher, lignes à valider, déséquilibre, écart avec le grand livre des
 * familles. Vide : rien ne s'oppose à la validation.
 */
export function bloquants(p: {
  synthese: readonly LigneDeSynthese[];
  notes: readonly NoteDeTransition[];
  ecartsClients: readonly EcartClient[];
}): Bloquant[] {
  const out: Bloquant[] = [];

  const aTrancher = p.notes.filter((n) => !n.validee || n.categorie === 'needs_review').length;
  if (aTrancher > 0) {
    out.push({
      etiquette: '[reprise-nc-a-trancher]',
      message: `${pluriel(aTrancher, 'note de transition', 'notes de transition')} à valider ou à trancher`,
    });
  }

  let aValider = 0;
  let aRevoir = 0;
  let enAttente = 0;
  for (const s of p.synthese) {
    aValider += s.nbAValider;
    aRevoir += s.nbARevoir;
    enAttente += s.nbEnAttente;
  }
  if (aValider + aRevoir + enAttente > 0) {
    const morceaux = [
      aValider > 0 ? `${aValider} à valider` : null,
      aRevoir > 0 ? `${aRevoir} à revoir` : null,
      enAttente > 0 ? `${enAttente} en attente du chiffre de la comptable` : null,
    ].filter(Boolean);
    out.push({
      etiquette: '[reprise-lignes-a-valider]',
      message: `${pluriel(aValider + aRevoir + enAttente, 'ligne non validée', 'lignes non validées')} : ${morceaux.join(', ')}`,
    });
  }

  const e = equilibre(p.synthese);
  if (e.ecartValideCents !== 0 || e.debitValideCents === 0) {
    out.push({
      etiquette: '[reprise-desequilibre]',
      message: e.debitValideCents === 0
        ? 'aucun montant validé à l’actif'
        : `actif ${eur(e.debitValideCents)}, passif et fonds propres ${eur(e.creditValideCents)} : écart de ${eur(e.ecartValideCents)}`,
    });
  }

  if (p.ecartsClients.length > 0) {
    const cumul = p.ecartsClients.reduce((t, x) => t + Math.abs(x.ecartCents), 0);
    out.push({
      etiquette: '[reprise-controle-clients]',
      message: `${pluriel(p.ecartsClients.length, 'famille', 'familles')} dont la reprise diffère du grand livre des familles (${eur(cumul)} d’écarts cumulés)`,
    });
  }

  return out;
}

/**
 * Les rubriques saisies qui n'ont encore aucune ligne. Ce n'est pas un refus
 * de la base — une ASBL peut n'avoir ni immobilisation ni subside à recevoir —
 * mais c'est la liste de ce que la comptable doit encore fournir ou écarter.
 */
export function rubriquesSansLigne(
  rubriques: readonly Rubrique[],
  synthese: readonly LigneDeSynthese[],
): Rubrique[] {
  const presentes = new Set(synthese.map((s) => s.rubrique));
  return rubriques
    .filter((r) => !r.calculee && !presentes.has(r.code))
    .sort((a, b) => a.ordre - b.ordre);
}

export interface GroupeDeRubrique {
  rubrique: string;
  libelle: string;
  ordre: number;
  calculee: boolean | null;
  lignes: LigneDeSynthese[];
  debitRetenuCents: number;
  creditRetenuCents: number;
  aTraiter: number;
}

/** La synthèse regroupée par rubrique, dans l'ordre des rubriques. */
export function parRubrique(synthese: readonly LigneDeSynthese[]): GroupeDeRubrique[] {
  const groupes = new Map<string, GroupeDeRubrique>();
  for (const s of synthese) {
    let g = groupes.get(s.rubrique);
    if (!g) {
      g = {
        rubrique: s.rubrique,
        libelle: s.rubriqueLibelle ?? s.rubrique,
        ordre: s.rubriqueOrdre ?? 999,
        calculee: s.calculee,
        lignes: [],
        debitRetenuCents: 0,
        creditRetenuCents: 0,
        aTraiter: 0,
      };
      groupes.set(s.rubrique, g);
    }
    g.lignes.push(s);
    if (s.sens === 'debit') g.debitRetenuCents += s.totalRetenuCents;
    else g.creditRetenuCents += s.totalRetenuCents;
    g.aTraiter += s.nbAValider + s.nbARevoir + s.nbEnAttente;
  }
  return [...groupes.values()]
    .map((g) => ({ ...g, lignes: [...g.lignes].sort((a, b) => a.compteCode.localeCompare(b.compteCode) || a.sens.localeCompare(b.sens)) }))
    .sort((a, b) => a.ordre - b.ordre);
}

// ---------------------------------------------------------------------------
//  Les saisies
// ---------------------------------------------------------------------------

function commencePar(code: string, prefixes: readonly string[]): boolean {
  return prefixes.some((p) => code.startsWith(p));
}

/** 407000 et 409000 naissent du traitement d'une créance, jamais d'une saisie. */
export const COMPTES_DE_TRAITEMENT: readonly string[] = ['407000', '409000'] as const;

/**
 * Le compte est-il admis dans la rubrique ? Le miroir de
 * `compta.reprise_lignes_saisie` (VALIDÉ ÉCLOSION, 26/09/2026) : un compte de
 * bilan (classes 1 à 5), hors comptes de traitement ; de la rubrique quand
 * elle en restreint les comptes ; « autres actifs » et « autres passifs »
 * admettent tout compte de bilan, sauf ceux d'une rubrique calculée (40, 46),
 * qui ont leur rubrique. Ce n'est pas une liste fermée.
 */
export function compteAdmis(
  rubrique: Rubrique,
  compte: CompteDeBilan,
  rubriques: readonly Rubrique[],
): boolean {
  if (!(compte.classe >= 1 && compte.classe <= 5)) return false;
  if (COMPTES_DE_TRAITEMENT.includes(compte.code)) return false;
  if (rubrique.prefixes !== null) return commencePar(compte.code, rubrique.prefixes);
  const calcules = rubriques.filter((r) => r.calculee).flatMap((r) => r.prefixes ?? []);
  return !commencePar(compte.code, calcules);
}

export function comptesDeLaRubrique(
  rubrique: Rubrique,
  comptes: readonly CompteDeBilan[],
  rubriques: readonly Rubrique[],
): CompteDeBilan[] {
  return comptes.filter((c) => compteAdmis(rubrique, c, rubriques));
}

export interface Saisie {
  rubrique: string;
  compteCode: string;
  sens: Sens;
  /** `null` : la ligne attend le chiffre de la comptable. */
  montantCents: number | null;
  justification: string;
  tiersId: string | null;
  /** Le type du tiers choisi, quand l'écran le connaît (une famille ne se saisit pas en client). */
  tiersType?: string | null;
  libelle: string;
}

/** Ce qui empêche d'enregistrer une saisie ; vide, elle part. */
export function controlerSaisie(
  s: Saisie,
  rubriques: readonly Rubrique[],
  comptes: readonly CompteDeBilan[],
): string[] {
  const refus: string[] = [];
  const rubrique = rubriques.find((r) => r.code === s.rubrique);
  const compte = comptes.find((c) => c.code === s.compteCode);
  if (!rubrique) refus.push('Choisir une rubrique.');
  if (!compte) refus.push('Choisir un compte.');
  if (rubrique && compte && !compteAdmis(rubrique, compte, rubriques)) {
    refus.push(`Le compte ${compte.code} ne relève pas de la rubrique « ${rubrique.libelle} ».`);
  }
  if (compte?.tiersObligatoire && !s.tiersId) {
    refus.push(`Le compte ${compte.code} porte un tiers : choisir ou créer le tiers.`);
  }
  if (rubrique?.saisie === 'hors_cdv' && s.tiersType === 'famille') {
    refus.push('Une famille CDV vient du calcul, recoupé avec le grand livre des familles : elle ne se saisit pas.');
  }
  if (s.montantCents !== null && !(Number.isSafeInteger(s.montantCents) && s.montantCents >= 0)) {
    refus.push('Un montant d’ouverture est positif ou nul ; un solde de sens contraire se saisit dans l’autre sens.');
  }
  if (s.justification.trim() === '') {
    refus.push('Dire d’où vient le montant : extrait, procès-verbal de caisse, état du patrimoine, facture.');
  }
  return refus;
}

// ---------------------------------------------------------------------------
//  Le traitement d'une créance d'ouverture
// ---------------------------------------------------------------------------

/**
 * Ce qu'il advient, à l'ouverture, d'une créance client (400000) au 31/12/2025
 * (VALIDÉ ÉCLOSION, 26/09/2026) :
 *
 * - **douteuse** : elle existe toujours juridiquement mais est compromise →
 *   407000, avec sa réduction de valeur en 409000 (au plus le douteux). Pas de
 *   charge 634000 en 2026 : la dépréciation appartient au 31/12/2025 ;
 * - **éteinte** : déjà définitivement abandonnée avant l'ouverture → elle n'y
 *   entre pas, et ne se maintient pas artificiellement en 407000 / 409000.
 *
 * Le reste demeure en 400000. Le choix se fait à la validation, justifié.
 */
export interface Traitement {
  montantCents: number;
  douteuxCents: number;
  reductionValeurCents: number;
  eteintCents: number;
  justification: string;
}

export interface EffetsDuTraitement {
  normalCents: number;
  douteuxCents: number;
  reductionValeurCents: number;
  eteintCents: number;
}

export function effetsDuTraitement(t: Traitement): EffetsDuTraitement {
  return {
    normalCents: t.montantCents - t.douteuxCents - t.eteintCents,
    douteuxCents: t.douteuxCents,
    reductionValeurCents: t.reductionValeurCents,
    eteintCents: t.eteintCents,
  };
}

/** Le miroir de `valider_creance_d_ouverture` et du gardien des lignes. */
export function controlerTraitement(t: Traitement): string[] {
  const refus: string[] = [];
  const montants = [t.montantCents, t.douteuxCents, t.reductionValeurCents, t.eteintCents];
  if (!montants.every((m) => Number.isSafeInteger(m) && m >= 0)) {
    refus.push('Les montants sont des centimes positifs ou nuls.');
    return refus;
  }
  if (t.reductionValeurCents > t.douteuxCents) {
    refus.push('La réduction de valeur ne dépasse pas la part douteuse.');
  }
  if (t.douteuxCents + t.eteintCents > t.montantCents) {
    refus.push(`Douteux et éteint ne dépassent pas la créance (${eur(t.montantCents)}).`);
  }
  if ((t.douteuxCents > 0 || t.eteintCents > 0) && t.justification.trim() === '') {
    refus.push('Une créance douteuse ou éteinte à l’ouverture dit pourquoi.');
  }
  return refus;
}

// ---------------------------------------------------------------------------
//  Les notes de transition
// ---------------------------------------------------------------------------

export interface DecisionNc {
  categorie: CategorieNc;
  ajustementCents: number;
  detteCents: number;
  correctionCents: number;
  justification: string;
}

/** Le partage par défaut quand on choisit une catégorie : toute la note y va. */
export function repartitionParDefaut(
  categorie: CategorieNc,
  montantCents: number,
): Pick<DecisionNc, 'ajustementCents' | 'detteCents' | 'correctionCents'> {
  return {
    ajustementCents: categorie === 'opening_receivable_adjustment' ? montantCents : 0,
    detteCents: categorie === 'opening_family_liability' ? montantCents : 0,
    correctionCents: categorie === 'current_year_prior_period_correction' ? montantCents : 0,
  };
}

/** Le miroir de `reprise_nc_controle` et de `valider_nc_de_transition`. */
export function controlerDecision(note: NoteDeTransition, d: DecisionNc): string[] {
  const refus: string[] = [];
  if (d.categorie === 'needs_review') {
    refus.push('Valider, c’est trancher : choisir une catégorie.');
    return refus;
  }
  const montants = [d.ajustementCents, d.detteCents, d.correctionCents];
  if (!montants.every((m) => Number.isSafeInteger(m) && m >= 0)) {
    refus.push('Les montants sont des centimes positifs ou nuls.');
  }
  if (d.ajustementCents + d.detteCents + d.correctionCents !== note.montantCents) {
    refus.push(`Ajustement + dette + correction doivent reconstituer la note : ${eur(note.montantCents)}.`);
  }
  if (d.categorie === 'current_year_prior_period_correction' && (d.ajustementCents > 0 || d.detteCents > 0)) {
    refus.push('Une correction d’exercice antérieur ne touche pas l’ouverture.');
  }
  if (d.categorie !== 'current_year_prior_period_correction' && d.correctionCents > 0) {
    refus.push('Un ajustement ou une dette d’ouverture ne porte pas de correction 2026.');
  }
  if (d.categorie !== note.categorieProposee && d.justification.trim() === '') {
    refus.push('Catégorie différente de la proposition : dire pourquoi.');
  }
  return refus;
}
